import Button from '@components/button/Button';
import InputWithFormItem from '@components/inputWithFormItem/InputWithFormItem';
import Label from '@components/inputWithFormItem/Label';
import Modal from '@components/modal/Modal';
import SelectBoxReams, {
  IOption,
} from '@components/selectBoxReams/SelectBoxReams';
import { Form, Space, Typography, notification } from 'antd';
import classNames from 'classnames';
import { head, isEmpty, uniq } from 'ramda';
import React, { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { useContactCustomerSupport } from '../../api/contactCustomerSupport';
import { features } from '../../pages/dashboards/dashboardFramework/helpers/features';

interface ContactSupportProps {
  openContactSupport: boolean;
  setOpenContactSupport: (open: boolean) => void;
  globalFeatures: string[];
}

const { Title, Text } = Typography;

const getSupportedProductsList = (globalFeatures: string[]) => {
  const supportedProducts = ['reams'];

  if (
    globalFeatures.includes(features.RETAIL_CATCHMENT_ANALYSIS) ||
    globalFeatures.includes(features.RETAIL_MARKET_ANALYSIS)
  ) {
    supportedProducts.push('retail');
  }
  supportedProducts.push('others');

  return uniq(supportedProducts);
};

const ContactSupport: React.FC<ContactSupportProps> = ({
  openContactSupport,
  setOpenContactSupport,
  globalFeatures,
}) => {
  const { t } = useTranslation('common', { keyPrefix: 'contactSupportForm' });

  const { contactCustomerSupport, isLoading } = useContactCustomerSupport();

  const [isSupportSubmitted, setIsSupportSubmitted] = useState<boolean>(false);
  const [form] = Form.useForm();

  const supportedProducts = useMemo(
    () => getSupportedProductsList(globalFeatures),
    [globalFeatures],
  );

  const onClickCancel = () => {
    setOpenContactSupport(false);
    form.resetFields();
    setIsSupportSubmitted(false);
  };

  const onSubmitRequest = (formData: any) => {
    contactCustomerSupport({
      supportReason: `Feature: ${formData.productFeature}
          Details: ${formData.supportDetails}`,
    })
      .then(() => {
        setIsSupportSubmitted(true);
      })
      .catch(() => {
        setIsSupportSubmitted(false);
        notification.error({
          title: t('supportRequestFailedErrorMessage'),
          description: t('supportRequestFailedErrorDescription'),
          placement: 'top',
        });
      });
  };

  const initialSelectedProduct = useMemo(
    () => (!isEmpty(supportedProducts) ? head(supportedProducts) : undefined),
    [supportedProducts],
  );

  const productDropdownDataset: IOption[] = useMemo(
    () =>
      supportedProducts.map((featureItem) => ({
        key: featureItem,
        value: featureItem,
        label: t(featureItem),
      })),
    [supportedProducts, t],
  );

  const renderSupportForm = () => (
    <div
      className={classNames('tw-w-[516px]')}
      style={{
        borderRadius: '20px',
      }}
    >
      <Space orientation="vertical" className="tw-gap-3">
        <Title level={2} className="!tw-m-0 tw-text-center">
          {t('contactSupportFormHeading')}
        </Title>
        <Text className="tw-text-weaker tw-text-center">
          {t('contactSupportFormDescription')}
        </Text>

        <Form
          form={form}
          layout="vertical"
          className="tw-w-full"
          onFinish={onSubmitRequest}
          requiredMark={false}
        >
          <Form.Item
            name="productFeature"
            label={<Label label={t('selectProductLabel')} />}
            className="tw-w-full"
            initialValue={initialSelectedProduct}
            rules={[{ required: true }]}
          >
            <SelectBoxReams
              value={initialSelectedProduct}
              options={productDropdownDataset}
            />
          </Form.Item>

          <InputWithFormItem
            inputName="supportDetails"
            type="textArea"
            label={t('requestDetailLabel')}
            rows={5}
            rules={[
              {
                required: true,
                message: t('detailRequiredErrorMessage'),
              },
              {
                max: 1500,
                message: t('detailMaxLengthValidationMessage', {
                  maxLength: 1500,
                }),
              },
            ]}
          />

          <Button
            fullWidth
            submit
            type="primary"
            size="middle"
            label={t('sendRequestBtnTitle')}
            loading={isLoading}
          />
        </Form>
      </Space>
    </div>
  );

  const renderRequestSubmitted = () => (
    <div
      className={classNames('tw-w-[516px]')}
      style={{
        borderRadius: '20px',
      }}
    >
      <Space orientation="vertical" className="tw-gap-3">
        <Title level={2} className="!tw-m-0 tw-text-center">
          {t('requestSendSuccessTitle')}
        </Title>
        <Trans
          components={{
            text: <Text className="tw-text-weaker tw-text-center" />,
          }}
        >
          {t('requestSendSuccessDescription')}
        </Trans>

        <Button
          fullWidth
          size="middle"
          type="primary"
          onClick={onClickCancel}
          label={t('continueAnalysis')}
        />
      </Space>
    </div>
  );

  return (
    <Modal
      isOpen={openContactSupport}
      title={t('contactSupportFormTitle')}
      renderModalContent={() =>
        isSupportSubmitted ? renderRequestSubmitted() : renderSupportForm()
      }
      onHandleCancel={onClickCancel}
      renderFooter={false}
    />
  );
};

const mapStateToProps = (state: any) => ({
  globalFeatures: state?.user?.globalFeatures || [],
});

export default connect(mapStateToProps)(ContactSupport);
